// The custom element both hosts consume: <ask-jumino-chat>. Attributes serve
// a plain HTML page; the .config property and imperative methods serve the portal.

import { h, render } from 'preact';
import { App, ChatBridge } from './App';
import { Launcher } from './components/Launcher';
import { FloatingPanel } from './components/FloatingPanel';
import { defaultStorageKind, type AskJuminoConfig, type WidgetLayout } from './config';
import { clearStoredSession, SessionStore } from './state/sessionStore';
import { KbChatClient } from './api/client';
import widgetCss from './styles/widget.scss?inline';

type AttrConfig = Pick<
  AskJuminoConfig,
  'baseUrl' | 'mode' | 'layout' | 'storageKey' | 'storage'
> & {
  accent?: string;
};

function readStorageAttr(raw: string | null): AskJuminoConfig['storage'] {
  return raw === 'session' || raw === 'local' ? raw : undefined;
}

function readAttrConfig(el: HTMLElement): AttrConfig {
  return {
    baseUrl: el.getAttribute('base-url') ?? '',
    mode: (el.getAttribute('mode') as AskJuminoConfig['mode']) ?? 'technical-sales',
    layout: (el.getAttribute('layout') as WidgetLayout) ?? 'panel',
    storageKey: el.getAttribute('storage-key') ?? 'ask-jumino-conversation',
    // Only the two known values count as an override; anything else (including a
    // typo) falls through to defaultStorageKind() rather than silently picking one.
    storage: readStorageAttr(el.getAttribute('storage')),
    accent: el.getAttribute('accent') ?? undefined,
  };
}

export class AskJuminoChatElement extends HTMLElement {
  static observedAttributes = [
    'base-url',
    'mode',
    'layout',
    'open',
    'storage-key',
    'storage',
    'accent',
  ];

  private root: ShadowRoot;
  private mountPoint: HTMLDivElement;
  private explicitConfig: Partial<AskJuminoConfig> = {};
  private isOpen = false;
  // Populated by App (via useChat); lets imperative methods reach live chat state.
  private bridge: ChatBridge = {};
  private connected = false;

  constructor() {
    super();
    this.root = this.attachShadow({ mode: 'open' });
    const style = document.createElement('style');
    style.textContent = widgetCss;
    this.mountPoint = document.createElement('div');
    // Set directly (not via CSS) so the height:100% chain to .aj-drawer can't get lost.
    this.mountPoint.style.height = '100%';
    this.mountPoint.style.display = 'flex';
    this.mountPoint.style.flexDirection = 'column';
    this.root.append(style, this.mountPoint);
  }

  get config(): AskJuminoConfig {
    const attrs = readAttrConfig(this);
    const merged: AskJuminoConfig = {
      baseUrl: this.explicitConfig.baseUrl ?? attrs.baseUrl,
      mode: this.explicitConfig.mode ?? attrs.mode,
      layout: this.explicitConfig.layout ?? attrs.layout,
      storageKey: this.explicitConfig.storageKey ?? attrs.storageKey,
      getHeaders: this.explicitConfig.getHeaders,
      resumePrompt: this.explicitConfig.resumePrompt,
      theme: this.explicitConfig.theme,
      strings: this.explicitConfig.strings,
    };
    // After mode/layout, since the default is derived from them.
    merged.storage = this.explicitConfig.storage ?? attrs.storage ?? defaultStorageKind(merged);
    if (attrs.accent && !merged.theme?.accent) {
      merged.theme = { ...merged.theme, accent: attrs.accent };
    }
    return merged;
  }

  set config(value: Partial<AskJuminoConfig>) {
    this.explicitConfig = { ...this.explicitConfig, ...value };
    this.applyTheme();
    this.renderApp();
  }

  connectedCallback(): void {
    this.connected = true;
    this.dataset.layout = this.config.layout;
    this.applyTheme();
    if (this.config.layout === 'floating') {
      // Read at connect time, not render time — attribute changes shouldn't
      // silently reopen a panel the visitor already closed.
      this.isOpen = this.hasAttribute('open');
    } else {
      this.isOpen = true; // panel layout has no launcher; it's always "open"
    }
    this.renderApp();
    this.dispatch('aj:ready', { version: __ASK_JUMINO_VERSION__ });
  }

  disconnectedCallback(): void {
    this.connected = false;
    // Triggers the mounted App's cleanup effect (teardown()) via Preact's own
    // unmount lifecycle — see state/useChat.ts's effect return.
    render(null, this.mountPoint);
  }

  attributeChangedCallback(name: string): void {
    if (!this.connected) return;
    if (name === 'layout') this.dataset.layout = this.config.layout;
    if (name === 'open') this.isOpen = this.hasAttribute('open');
    if (name === 'accent') this.applyTheme();
    this.renderApp();
  }

  // --- imperative API -------------------------------------------------------

  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;
    this.setAttribute('open', '');
    this.renderApp();
    this.dispatch('aj:open', { reason: 'api' });
  }

  close(): void {
    if (!this.isOpen) return;
    this.bridge.teardown?.();
    this.isOpen = false;
    this.removeAttribute('open');
    this.renderApp();
    this.dispatch('aj:close', { reason: 'api' });
  }

  toggle(): void {
    this.isOpen ? this.close() : this.open();
  }

  /** Drop the stored conversation; best-effort DELETE /sessions/{id}. */
  async clearSession(): Promise<void> {
    const storageKey = this.config.storageKey ?? 'ask-jumino-conversation';
    // The getter always resolves this; the fallback covers a hand-built config.
    const storageKind = this.config.storage ?? defaultStorageKind(this.config);
    const store = new SessionStore(storageKey, storageKind);
    const prev = store.getPrevious();
    if (prev) {
      const client = new KbChatClient({
        baseUrl: this.config.baseUrl,
        getHeaders: this.config.getHeaders,
      });
      await client.deleteSession(prev.sessionId).catch(() => undefined);
    }
    clearStoredSession(storageKey, storageKind);
  }

  /** Abandon the current conversation, start a fresh backend session. */
  async newConversation(): Promise<void> {
    this.bridge.startNew?.();
  }

  // --- internals -------------------------------------------------------------

  private applyTheme(): void {
    const theme = this.explicitConfig.theme;
    const accent = theme?.accent ?? this.getAttribute('accent') ?? undefined;
    // Derive the gradient from accent too, or overriding accent alone leaves
    // the header/buttons on the default teal while only links/spinner change.
    const derivedGradient = accent
      ? `linear-gradient(135deg, ${accent} 0%, ${accent} 100%)`
      : undefined;
    const map: Record<string, string | undefined> = {
      '--aj-accent': accent,
      '--aj-accent-hover': theme?.accentHover,
      '--aj-gradient': theme?.gradient ?? derivedGradient,
      '--aj-user-gradient': theme?.gradient ?? derivedGradient,
      '--aj-radius': theme?.radius,
      '--aj-font-family': theme?.fontFamily,
      '--aj-z-index': theme?.zIndex,
    };
    for (const [prop, val] of Object.entries(map)) {
      if (val) this.style.setProperty(prop, val);
    }
  }

  private dispatch(type: string, detail: unknown): void {
    this.dispatchEvent(new CustomEvent(type, { detail, bubbles: true, composed: true }));
  }

  private renderApp(): void {
    const cfg = this.config;
    if (!cfg.baseUrl) {
      // Missing base-url is a host-integration bug — fail loudly, don't silently no-op.
      // eslint-disable-next-line no-console
      console.error('<ask-jumino-chat> requires a base-url attribute or config.baseUrl.');
      return;
    }

    const showPanel = cfg.layout === 'panel' || this.isOpen;
    const appEl = h(App, {
      config: cfg,
      onClose: () => this.close(),
      bridge: this.bridge,
      chatCallbacks: {
        onSessionCreated: (id: string) => this.dispatch('aj:session-created', { sessionId: id }),
        onAnswer: (info: unknown) => this.dispatch('aj:answer', info),
        onError: (info: unknown) => this.dispatch('aj:error', info),
      },
      // Mirrored onto :host itself — fullscreen breaks the custom element out
      // of whatever box the host page put it in. See widget.scss.
      onFullscreenChange: (isFullscreen: boolean) => {
        this.toggleAttribute('data-fullscreen', isFullscreen);
        this.dispatch('aj:fullscreen', { fullscreen: isFullscreen });
      },
    });

    const tree = h(
      'div',
      // Continues the height:100% chain from the constructor's mountPoint styling.
      {
        class: 'aj-widget-root',
        style: { display: 'flex', flexDirection: 'column', height: '100%' },
      },
      cfg.layout === 'floating' && !this.isOpen
        ? h(Launcher, { onClick: () => this.open() })
        : null,
      showPanel
        ? cfg.layout === 'floating'
          ? h(FloatingPanel, { children: appEl })
          : appEl
        : null,
    );
    render(tree, this.mountPoint);
  }
}
